import { ModalName } from '@share/enums';
import { market } from '@state/market';
import { toast } from 'react-toastify';
import { toggleBootstrapModal } from '@util/helper';
import { useAPI } from 'src/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { user } from '@state/user';
import { web3 } from '@state/web3';
import Countdown, { CountdownRenderProps } from 'react-countdown';
import ErrorMsg from '@com/utils/ErrorMsg';
import Swal from 'sweetalert2';

interface Params {
  tokenId: number;
  collectionAddress: string;
  contractChainId: number;
}

interface FormInputs {
  email_code: string;
  wallet_address: string;
}

function TreasuryToWalletModal(param: Params): JSX.Element {
  /* Define state / hook */
  const { tokenId, collectionAddress, contractChainId } = param;
  const { userData } = user.useContainer();
  const { getProvider } = web3.useContainer();
  const { payment } = market.useContainer();
  const { t } = useTranslation();
  const { getWithdrawNftCode, withdrawNft } = useAPI();
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
    trigger,
    // Use onBlur because if input wrong address and get code, isAbleToGetCode will be false
    // and get code btn will be disable, then cannot not trigger validate
  } = useForm<FormInputs>({ mode: 'onBlur' });

  /* Custom Data Object for render or other usages */
  const walletAddress = watch('wallet_address');
  const isAbleToGetCode =
    !isSending &&
    // Make sure user can only get code after inputting correct wallet address
    walletAddress &&
    errors?.wallet_address === undefined;

  /* render control function / logic */
  const connectWallet = async () => {
    const provider = await getProvider();
    if (!provider) return;
    const address = await provider.getSigner().getAddress();
    setValue('wallet_address', address);
  };

  const handleSendEmail = useDebouncedCallback(
    async () => {
      if (!isAbleToGetCode) return; // Prevent bypassing disabled button
      if (!(await trigger('wallet_address'))) return;

      const response = await getWithdrawNftCode({
        chainId: contractChainId,
        collectionAddress,
        tokenId,
        receiver: walletAddress,
      });
      if (response) {
        setIsSending(true);
        toast.success(
          t('toastMessage.verifyEmailSent', { email: userData?.user?.email })
        );
        setTimeout(() => {
          setIsSending(false);
        }, 60 * 1000);
      }
    },
    500,
    { leading: true, trailing: false }
  );

  const handleWithdraw = useDebouncedCallback(
    async (formData: FormInputs) => {
      const result = await Swal.mixin({
        background: 'var(--bg-section2)',
        color: 'var(--primary-color2)',
        customClass: {
          popup: 'sweet-alert-popup',
          container: 'sweet-alert-container',
          confirmButton: 'sweet-alert-confirm-btn',
          cancelButton: 'sweet-alert-cancel-btn',
        },
        confirmButtonColor: 'var(--primary-color3)',
      }).fire({
        title: t('sweetAlert.confirmTitle'),
        text: t('sweetAlert.confirmTakeoutText', {
          address: formData.wallet_address,
        }),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: t('sweetAlert.confirm'),
        cancelButtonText: t('sweetAlert.cancel'),
        reverseButtons: true,
      });

      if (result.isConfirmed) {
        const isLogin = await payment();
        if (!isLogin) return;

        setIsLoading(true);
        await withdrawNft({
          chainId: contractChainId,
          collectionAddress,
          tokenId,
          receiver: formData.wallet_address,
          code: formData.email_code,
        });

        toast.success(t('toastMessage.ownerTransferred'));
        toggleBootstrapModal(ModalName.WITHDRAW);
        setIsLoading(false);
      }
    },
    500,
    { leading: true, trailing: false }
  );
  /* Styling */

  /* Render */
  return (
    <div
      aria-hidden
      className="modal fade popup"
      id={ModalName.WITHDRAW}
      role="dialog"
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <button
            className="close"
            type="button"
            onClick={() => {
              if (!isLoading) toggleBootstrapModal(ModalName.WITHDRAW);
            }}
          >
            <span aria-hidden>&times;</span>
          </button>
          <form onSubmit={handleSubmit(handleWithdraw)}>
            <div className="modal-body pd-40">
              <h3>{`${t('modal.treasuryToWallet', { id: tokenId })}`}</h3>
              <hr />

              <p>{t('modal.chooseReceiveWallet')}</p>
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <input
                  aria-required
                  aria-label={t('modal.chooseReceiveWallet')}
                  className="form-control mt-3 col-sm-7 col-12"
                  disabled={isSending}
                  id="wallet_address"
                  type="text"
                  {...register('wallet_address', {
                    required: {
                      value: true,
                      message: t('modal.walletRequired'),
                    },
                    pattern: {
                      value: /^\s*0x[a-fA-F0-9]{40}\s*$/g,
                      message: t('modal.inputValidWallet'),
                    },
                  })}
                />
                {!walletAddress && (
                  <button
                    aria-required
                    aria-label={t('modal.connect')}
                    className="btn btn-primary mt-3 col-sm-4 col-12"
                    disabled={isSending}
                    type="button"
                    onClick={() => {
                      connectWallet();
                    }}
                  >
                    {t('modal.connect')}
                  </button>
                )}
              </div>
              <ErrorMsg errors={errors} name="wallet_address" />

              <p className="mt-5">{t('modal.emailVerificationCode')}</p>
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <input
                  aria-required
                  aria-label={t('modal.emailVerificationCode')}
                  className="form-control mt-3 col-sm-7 col-12"
                  id="email_code"
                  type="text"
                  {...register('email_code', {
                    required: {
                      value: true,
                      message: t('modal.verificationCodeRequired'),
                    },
                    pattern: {
                      value: /^.{6}$/,
                      message: t('modal.verificationCodePattern'),
                    },
                  })}
                />
                <button
                  aria-required
                  aria-label={t('modal.getCode')}
                  className="btn btn-primary mt-3 col-sm-4 col-12"
                  disabled={!isAbleToGetCode}
                  type="button"
                  onClick={() => {
                    handleSendEmail();
                  }}
                >
                  {isSending ? (
                    <Countdown
                      date={Date.now() + 59 * 1000}
                      renderer={(props: CountdownRenderProps) => {
                        return (
                          <span>{`${props.minutes}:${props.seconds}`}</span>
                        );
                      }}
                    />
                  ) : (
                    t('modal.getCode')
                  )}
                </button>
              </div>
              <ErrorMsg errors={errors} name="email_code" />

              <button
                aria-required
                aria-label={t('modal.confirm')}
                className="mt-5 btn btn-primary"
                disabled={isLoading}
                type="submit"
              >
                {t('modal.confirm')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default TreasuryToWalletModal;
